import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { authAPI, profileAPI } from '../api/api.js';
import { clearEmailVerificationReminderSessionDismiss } from '../Utils/emailVerificationReminder.js';
import {
    clearRequireStorePick,
    markRequireStorePickOnLogin,
} from '../Utils/storePickSession.js';
import {
    getRetryAfterMs,
    isDefinitiveAuthFailure,
    isRetryableServerError,
    isTransientNetworkError,
} from '../Utils/authErrors.js';
import { clearAuthToken, getAuthToken, setAuthToken, usesPersistentAuthStorage } from '../Utils/authToken.js';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);
    const [authCheckFailed, setAuthCheckFailed] = useState(false);

    useEffect(() => {
        checkAuth();
    }, []);

    const setUserFromProfile = async (fallbackUser = null, loginRequiresPasswordHint) => {
        try {
            const profileRes = await profileAPI.getProfile();
            setUser(profileRes.data.admin);
            setPasswordChangeRequired(!!profileRes.data.passwordChangeRequired);
        } catch (err) {
            setUser(fallbackUser);
            if (loginRequiresPasswordHint !== undefined) {
                setPasswordChangeRequired(!!loginRequiresPasswordHint);
            } else if (!fallbackUser) {
                setPasswordChangeRequired(false);
            }
        }
    };

    const checkAuth = async () => {
        const token = getAuthToken();
        if (!token) {
            setLoading(false);
            return;
        }

        setLoading(true);

        const maxAttempts = 3;
        for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
            try {
                const response = await profileAPI.getProfile();
                setAuthCheckFailed(false);
                setUser(response.data.admin);
                setPasswordChangeRequired(!!response.data.passwordChangeRequired);
                setIsAuthenticated(true);
                setAuthToken(token, { rememberMe: usesPersistentAuthStorage() });
                setLoading(false);
                return;
            } catch (error) {
                if (isDefinitiveAuthFailure(error)) {
                    clearAuthToken();
                    setUser(null);
                    setIsAuthenticated(false);
                    setPasswordChangeRequired(false);
                    setAuthCheckFailed(false);
                    setLoading(false);
                    return;
                }

                // A rate limit or a server hiccup is not a sign-out. Retrying, then surfacing
                // an error, keeps a valid session instead of bouncing the admin to /login.
                const retryable =
                    isTransientNetworkError(error) || isRetryableServerError(error);

                if (retryable && attempt < maxAttempts) {
                    await new Promise((resolve) =>
                        setTimeout(resolve, getRetryAfterMs(error, attempt * 1000)),
                    );
                    continue;
                }

                console.warn('Profile check failed; keeping session token:', error?.message || error);
                setUser(null);
                setIsAuthenticated(false);
                setPasswordChangeRequired(false);
                setAuthCheckFailed(retryable);
                setLoading(false);
                return;
            }
        }
    };

    const login = async (email, password, rememberMe = true) => {
        setAuthCheckFailed(false);
        const response = await authAPI.login(email, password);

        if (response.data.requiresTwoFactor) {
            return {
                requiresTwoFactor: true,
                tempToken: response.data.tempToken,
                requiresPasswordChange: !!response.data.requiresPasswordChange,
            };
        }

        setAuthToken(response.data.token, { rememberMe });
        markRequireStorePickOnLogin();
        await setUserFromProfile(response.data.admin, response.data.requiresPasswordChange);
        setIsAuthenticated(true);
        return { success: true, requiresPasswordChange: !!response.data.requiresPasswordChange };
    };

    const verify2FA = async (code, tempToken, useBackupCode = false) => {
        const response = await authAPI.verify2FA(code, tempToken, useBackupCode);
        setAuthToken(response.data.token, { rememberMe: true });
        markRequireStorePickOnLogin();
        await setUserFromProfile(response.data.admin, response.data.requiresPasswordChange);
        setIsAuthenticated(true);
        return { success: true, requiresPasswordChange: !!response.data.requiresPasswordChange };
    };

    const register = async (userData) => {
        const response = await authAPI.register(userData);

        if (response.data.requiresEmailVerification) {
            return {
                requiresEmailVerification: true,
                email: response.data.email || userData.email,
                maskedEmail: response.data.maskedEmail,
            };
        }

        if (response.data.token) {
            setAuthToken(response.data.token, { rememberMe: true });
            markRequireStorePickOnLogin();
            await setUserFromProfile(response.data.admin, response.data.requiresPasswordChange);
            setIsAuthenticated(true);
        }

        return response.data;
    };

    const verifyEmailCode = async (email, code) => {
        const response = await authAPI.verifyEmailCode(email, code);
        setAuthToken(response.data.token, { rememberMe: true });
        markRequireStorePickOnLogin();
        await setUserFromProfile(response.data.admin, response.data.requiresPasswordChange);
        setIsAuthenticated(true);
        return {
            success: true,
            requiresPasswordChange: !!response.data.requiresPasswordChange,
        };
    };

    const logout = async () => {
        try {
            await authAPI.logout();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            clearAuthToken();
            localStorage.removeItem('selectedStoreMode');
            sessionStorage.removeItem('selectedStoreMode');
            localStorage.removeItem('selectedStoreId');
            sessionStorage.removeItem('selectedStoreId');
            localStorage.removeItem('selectedWorkspaceId');
            sessionStorage.removeItem('selectedWorkspaceId');
            clearRequireStorePick();
            clearEmailVerificationReminderSessionDismiss();
            setUser(null);
            setIsAuthenticated(false);
            setPasswordChangeRequired(false);
            setAuthCheckFailed(false);
        }
    };

    const clearPasswordChangeGate = useCallback(() => {
        setPasswordChangeRequired(false);
    }, []);

    const updateUser = useCallback((userData) => {
        setUser(userData);
    }, []);

    const value = {
        user,
        loading,
        isAuthenticated,
        passwordChangeRequired,
        authCheckFailed,
        setPasswordChangeRequired,
        clearPasswordChangeGate,
        setIsAuthenticated,
        login,
        verify2FA,
        register,
        verifyEmailCode,
        logout,
        updateUser,
        checkAuth,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
    children: PropTypes.node,
};


